import { useRef, useState, type FC } from 'react';
import {
  Button, ComposedModal, ContainedList, ContainedListItem, Form, IconButton, ListItem, ModalBody, ModalFooter, ModalHeader,
  OverflowMenu, OverflowMenuItem, TextInput, UnorderedList,
} from '@carbon/react';
import { Add, ArrowRight, Chat } from '@carbon/icons-react';

import type { DadosDaConversa } from '../../../logica/nyta/useConversaDaNyta';
import { SOBRE_A_CONVERSA } from '../../../logica/nyta/explicacoes';
import { MarcaDaNyta } from '../../marcas/MarcaDaNyta';
import { ConfirmarPerigo } from '../../padroes/ConfirmarPerigo';
import { RotuloDeIA } from '../../padroes/ia/RotuloDeIA';
import { ROTULOS } from '../../padroes/rotulos';
import { useDoPonto } from '../../padroes/useDoMd';
import { Vazio } from '../../padroes/Vazio';
import { Conversa } from './Conversa';
import styles from './PaginaDaNyta.module.scss';

// A PÁGINA DA NYTA (/artists/:id/nyta): as conversas à esquerda e a conversa aberta. O topo da
// conversa diz com quem se fala e leva o rótulo de IA amplo (a conversa inteira é IA); cada
// resposta leva o seu, focado. Abaixo do lg, as conversas abrem num modal pelo botão do topo.

/** Sem acesso: o dono vê a oferta; o convidado de um titular sem assinatura vê o aviso (sem venda). */
export const NytaTravada: FC<{ t: NonNullable<DadosDaConversa['travada']> }> = ({ t }) => (
  <Vazio
    pictograma={MarcaDaNyta}
    titulo={t.titulo}
    texto={t.texto}
    acao={(
      <div className={styles.travada}>
        {t.beneficios.length > 0 && <UnorderedList>{t.beneficios.map((b) => <ListItem key={b}>{b}</ListItem>)}</UnorderedList>}
        {t.apoio && <p className={styles.apoioDaTravada}>{t.apoio}</p>}
        {t.assinar && <div><Button kind='primary' size='md' renderIcon={ArrowRight} onClick={t.assinar}>{t.rotulo}</Button></div>}
      </div>
    )}
  />
);

type ConversaDaLista = DadosDaConversa['conversas'][number];

/** A lista de conversas. Renomear e excluir abrem FORA dela (no nível da página), para nenhum
 *  modal morar dentro de outro; o foco volta ao ⋮ que os abriu. */
const ListaDeConversas: FC<{
  c: DadosDaConversa;
  aoEscolher?: () => void;
  dentroDoModal?: boolean;
  pedirRenomear: (x: ConversaDaLista, origem: HTMLButtonElement | null) => void;
  pedirExcluir: (x: ConversaDaLista, origem: HTMLButtonElement | null) => void;
}> = ({ c, aoEscolher, dentroDoModal, pedirRenomear, pedirExcluir }) => {
  const menus = useRef<Record<string, HTMLButtonElement | null>>({});
  return (
    // No modal, o título do modal já diz "Conversas": a lista vai sem cabeçalho (e o "Nova conversa"
    // mora no corpo do modal).
    <ContainedList label={dentroDoModal ? undefined : 'Conversas'} aria-label={dentroDoModal ? 'Conversas' : undefined} kind='on-page' size='md' className={styles.lista}
      action={dentroDoModal ? undefined : <IconButton kind='ghost' size='sm' label='Nova conversa' onClick={() => { c.novaConversa(); aoEscolher?.(); }}><Add /></IconButton>}>
      {c.conversas.length === 0 && <ContainedListItem><span className={styles.apoio}>Suas conversas com a Nyta aparecem aqui.</span></ContainedListItem>}
      {c.conversas.map((x) => (
        <ContainedListItem
          key={x.id}
          renderIcon={Chat}
          className={x.aberta ? styles.aberta : undefined}
          onClick={() => { x.abrir(); aoEscolher?.(); }}
          action={(
            <OverflowMenu ref={(el: HTMLDivElement | null) => { menus.current[x.id] = (el?.matches('button') ? el : el?.querySelector('button')) as HTMLButtonElement | null; }} size='sm' flipped aria-label={`Opções de ${x.titulo}`} iconDescription={`Opções de ${x.titulo}`}>
              <OverflowMenuItem itemText='Renomear' onClick={() => pedirRenomear(x, menus.current[x.id])} />
              <OverflowMenuItem itemText='Excluir' isDelete onClick={() => pedirExcluir(x, menus.current[x.id])} />
            </OverflowMenu>
          )}
        >
          <span className={styles.itemDaLista}>
            <span className={styles.tituloDaConversa}>{x.titulo}</span>
            <span className={styles.apoio}>{x.quando}</span>
          </span>
        </ContainedListItem>
      ))}
    </ContainedList>
  );
};

export const PaginaDaNyta: FC<{ c: DadosDaConversa }> = ({ c }) => {
  const doLg = useDoPonto('lg');
  const doMd = useDoPonto('md');
  const [listaAberta, setListaAberta] = useState(false);
  const [renomeando, setRenomeando] = useState<{ x: ConversaDaLista; titulo: string } | null>(null);
  const [excluindo, setExcluindo] = useState<ConversaDaLista | null>(null);
  const [emCurso, setEmCurso] = useState(false);
  const origem = useRef<HTMLButtonElement | null>(null);
  // No celular a lista mora num modal: ele fecha antes de o próximo abrir.
  const botaoDasConversas = useRef<HTMLButtonElement | null>(null);
  // No celular o ⋮ some com o modal da lista: o foco volta ao botão "Conversas" do topo.
  const pedirRenomear = (x: ConversaDaLista, el: HTMLButtonElement | null) => { origem.current = doLg ? el : botaoDasConversas.current; setListaAberta(false); setRenomeando({ x, titulo: x.titulo }); };
  const pedirExcluir = (x: ConversaDaLista, el: HTMLButtonElement | null) => { origem.current = doLg ? el : botaoDasConversas.current; setListaAberta(false); setExcluindo(x); };
  const salvarNome = () => { if (renomeando?.titulo.trim()) { void renomeando.x.renomear(renomeando.titulo); setRenomeando(null); } };
  const titulo = c.conversas.find((x) => x.aberta)?.titulo ?? 'Nova conversa';

  if (c.travada) {
    return <div className={styles.paginaTravada}><h1 className='cds--visually-hidden'>Nyta</h1><NytaTravada t={c.travada} /></div>;
  }

  return (
    <div className={styles.pagina}>
      <h1 className='cds--visually-hidden'>Nyta</h1>
      {doLg && <aside className={styles.lateral} aria-label='Conversas com a Nyta'><ListaDeConversas c={c} pedirRenomear={pedirRenomear} pedirExcluir={pedirExcluir} /></aside>}
      <section className={styles.principal} aria-label='Conversa com a Nyta'>
        <header className={styles.topo}>
          {/* O "IA" é a marca da Nyta e o rótulo amplo (a conversa inteira é com uma IA, e ele explica). */}
          <RotuloDeIA sobre={SOBRE_A_CONVERSA} tamanho='xs' alinhar='bottom-start' />
          <span className={styles.quem}>
            <h2 className={styles.nome}>Nyta</h2>
            <span className={styles.apoioDoTopo}>{titulo}</span>
          </span>
          <span className={styles.acoesDoTopo}>
            {!doLg && <IconButton ref={botaoDasConversas} kind='ghost' size='sm' label='Conversas' onClick={() => setListaAberta(true)}><Chat /></IconButton>}
            {doMd
              ? <Button kind='ghost' size='sm' renderIcon={Add} onClick={c.novaConversa}>Nova conversa</Button>
              : <IconButton kind='ghost' size='sm' label='Nova conversa' onClick={c.novaConversa}><Add /></IconButton>}
          </span>
        </header>
        <div className={styles.corpo}><Conversa c={c} /></div>
      </section>
      {!doLg && (
        <ComposedModal open={listaAberta} size='sm' onClose={() => { setListaAberta(false); return true; }}>
          <ModalHeader label='Nyta' title='Conversas' iconDescription={ROTULOS.fechar} />
          <ModalBody>
            <div className={styles.novaNoModal}><Button kind='ghost' size='sm' renderIcon={Add} onClick={() => { c.novaConversa(); setListaAberta(false); }}>Nova conversa</Button></div>
            <ListaDeConversas c={c} dentroDoModal aoEscolher={() => setListaAberta(false)} pedirRenomear={pedirRenomear} pedirExcluir={pedirExcluir} />
          </ModalBody>
        </ComposedModal>
      )}

      <ComposedModal open={!!renomeando} size='sm' onClose={() => { setRenomeando(null); return true; }} launcherButtonRef={origem}>
        <ModalHeader label='Nyta' title='Renomear conversa' iconDescription={ROTULOS.fechar} />
        <ModalBody hasForm>
          <Form noValidate aria-label='Renomear conversa' onSubmit={(e) => { e.preventDefault(); salvarNome(); }}>
            <TextInput id='renomear-conversa' labelText='Nome da conversa' maxLength={80} data-modal-primary-focus value={renomeando?.titulo ?? ''}
              onChange={(e) => setRenomeando((r) => (r ? { ...r, titulo: e.target.value } : r))} />
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button kind='secondary' onClick={() => setRenomeando(null)}>Cancelar</Button>
          <Button kind='primary' disabled={!renomeando?.titulo.trim()} onClick={salvarNome}>Salvar</Button>
        </ModalFooter>
      </ComposedModal>

      <ConfirmarPerigo
        aberto={!!excluindo}
        rotulo='Nyta'
        titulo='Excluir conversa?'
        texto={`As mensagens de "${excluindo?.titulo ?? ''}" serão apagadas. Não dá para desfazer.`}
        acao='Excluir'
        manter='Manter conversa'
        emCurso={emCurso}
        devolverFocoPara={origem}
        aoConfirmar={async () => { if (!excluindo) return; setEmCurso(true); await excluindo.excluir(); setEmCurso(false); setExcluindo(null); }}
        aoFechar={() => setExcluindo(null)}
      />
    </div>
  );
};
